import { useState } from 'react'
import logo from '../assets/smobilpay-logo.png'
import { loginHref } from '../bff/client'
import { useI18n } from '../i18n'
import { Button } from '../ui'
import styles from './Landing.module.css'

export interface LandingProps {
  /** Shown after a completed sign-out, so the user gets confirmation rather
   *  than silently landing back where they started. */
  readonly signedOut?: boolean
}

/**
 * The only unauthenticated screen. It owns no credential fields by design:
 * Keycloak renders the password and TOTP screens, so a phishing lookalike
 * cannot be built by copying this page, and the SPA never touches a password.
 *
 * `/bff/login` is a real navigation, not a fetch — it 302s to Keycloak, which
 * a same-origin XHR could not follow.
 */
/**
 * Reasons the BFF can hand back on `/?error=`.
 *
 * Anything else is ignored rather than rendered: the value arrives in a URL
 * anybody can edit, so it selects a message we wrote, never supplies one.
 */
const ERROR_MESSAGES: Record<
  string,
  'landing.sessionExpired' | 'landing.signInFailed' | 'landing.organisationDisabled'
> = {
  session_expired: 'landing.sessionExpired',
  forbidden: 'landing.signInFailed',
  // FR-11: the sign-in was refused because the organisation is deactivated,
  // not because the credentials were wrong. Saying so is the difference
  // between a user retrying their password forever and telephoning the person
  // who can actually reactivate them.
  organisation_disabled: 'landing.organisationDisabled',
}

/**
 * Where sign-in should land the user: the path they are on, minus the BFF's own
 * `error` parameter.
 *
 * The BFF has always supported this — `GET /bff/login` reads `?returnTo=`,
 * checks it resolves to our own origin, seals it into the login transaction and
 * redirects there after the callback — and the SPA has always sent nothing, so
 * every sign-in took the default `/` and a pasted deep link was discarded
 * (MPAY-32122).
 *
 * `error` is stripped because it is the BFF's message to THIS render; carrying
 * it through a successful sign-in would greet the user with "that link had
 * expired" on the page they just reached. Every other parameter is the user's —
 * filters, a status, a date range — and is exactly what they came back for.
 *
 * Returns null at the root with nothing to preserve, so the common case sends a
 * bare `/bff/login` rather than a parameter that only repeats the default.
 */
export function returnToFromLocation(pathname: string, search: string): string | null {
  const params = new URLSearchParams(search)
  params.delete('error')
  const query = params.toString()
  const target = query ? `${pathname}?${query}` : pathname
  return target === '/' ? null : target
}

export function Landing({ signedOut = false }: LandingProps) {
  const { t, locale } = useI18n()
  const [redirecting, setRedirecting] = useState(false)
  /* Read once, at first render. The BFF redirects here with a reason when it
     cannot complete a sign-in — a password reset finished long after its login
     transaction expired, most often. Before this the parameter was ignored
     entirely and the user was returned to an unchanged sign-in page with no
     hint that anything had happened, or why they were back here. */
  const [reason] = useState(() => {
    if (typeof window === 'undefined') return null
    const raw = new URLSearchParams(window.location.search).get('error')
    return raw && raw in ERROR_MESSAGES ? ERROR_MESSAGES[raw] : null
  })

  return (
    <div className={styles.landing}>
      <section className={styles.panel}>
        <div className={styles.brand}>
          {/* The wordmark carries the company name, so it gets a real alt
              rather than aria-hidden; `app.name` below is the product
              descriptor, which is a different fact and stays as text. */}
          <img className={styles.brandMark} src={logo} alt={t('app.logoAlt')} />
          <span className={styles.brandName}>{t('app.name')}</span>
        </div>

        {signedOut && <p className={styles.signedOut}>{t('landing.signedOut')}</p>}
        {reason && (
          <p className={styles.notice} role="status">
            {t(reason)}
          </p>
        )}

        <h1 className={styles.title}>{t('landing.title')}</h1>
        <p className={styles.tagline}>{t('landing.tagline')}</p>

        <div className={styles.actions}>
          <Button
            size="lg"
            loading={redirecting}
            onClick={() => {
              setRedirecting(true)
              // The language goes with it: Keycloak renders the next screen
              // and cannot see the choice stored on this origin (MPAY-32208).
              window.location.assign(loginHref({
                returnTo: returnToFromLocation(window.location.pathname, window.location.search),
                uiLocales: locale,
              }))
            }}
          >
            {redirecting ? t('landing.signingIn') : t('landing.signIn')}
          </Button>
          <p className={styles.note}>{t('landing.mfaNote')}</p>
        </div>
      </section>
      <div className={styles.art} aria-hidden="true" />
    </div>
  )
}
